import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { GitBranch, MessageSquare, ClipboardList } from 'lucide-react';
import { useT } from '../contexts/LanguageContext';

/* Visual workflow diagrams for Complaint lifecycle and Service Provider PPM
 * Pure CSS/Tailwind — no external graph library. Each step has a colored
 * pill, an arrow, and a description. Branching points show side-paths.
 */

const COMPLAINT_FLOW = [
  { id: 'received',    label: '1. Complaint received',     desc: 'Tenant or staff raises a complaint via portal/app',   color: 'sky' },
  { id: 'triaged',     label: '2. Triaged',                 desc: 'CS Supervisor sets priority + assigns technician',    color: 'sky' },
  { id: 'inprogress',  label: '3. In progress',             desc: 'Technician on site / executing the fix',              color: 'amber' },
  { id: 'service',     label: '4. Service report submitted',desc: 'Hours, materials used, materials needed listed',      color: 'amber' },
  { id: 'matapprove',  label: '5. Materials approved',      desc: 'Manager approves any extra materials (if needed)',    color: 'amber', branch: 'optional' },
  { id: 'matissue',    label: '6. Materials issued',         desc: 'Storekeeper issues from stock (auto-decrement)',     color: 'amber', branch: 'optional' },
  { id: 'tenantsign',  label: '7. Tenant signs',             desc: 'Tenant signs off the report on glass',                color: 'emerald' },
  { id: 'invoice',     label: '8. Invoice raised',           desc: 'For chargeable scope, A/R invoice generated',          color: 'emerald', branch: 'optional' },
  { id: 'payment',     label: '9. Payment received',         desc: 'A/R payment recorded; invoice marked paid',            color: 'emerald', branch: 'optional' },
  { id: 'closed',      label: '10. Closed',                  desc: 'Complaint marked resolved + closed; KPIs updated',     color: 'emerald' },
];

const PPM_FLOW = [
  { id: 'amc',         label: '1. AMC contract signed',    desc: 'Service provider engaged with scope items + SLAs', color: 'sky' },
  { id: 'schedule',    label: '2. PPM scheduled',           desc: 'Per-asset frequency creates auto-recurring tasks',  color: 'sky' },
  { id: 'dispatched',  label: '3. Dispatch / due',           desc: 'Provider notified; technician assigned; SLA timer',color: 'amber' },
  { id: 'inspection',  label: '4. Inspection on site',       desc: 'Provider performs PM checklist + captures photos', color: 'amber' },
  { id: 'report',      label: '5. Service report logged',    desc: 'Provider files report (in_house or service_provider)', color: 'amber' },
  { id: 'sla',         label: '6. SLA evaluated',            desc: 'sla_actual_hours vs sla_target_hours -> sla_met flag',   color: 'amber' },
  { id: 'invoice',     label: '7. Provider invoice received',desc: 'Service provider sends invoice for the visit',     color: 'emerald' },
  { id: 'match',       label: '8. AP matching',              desc: 'Invoice matched against AMC line item + PO',         color: 'emerald' },
  { id: 'approve',     label: '9. Invoice approved',         desc: 'Manager / Accountant approves for payment',          color: 'emerald' },
  { id: 'payment',     label: '10. Payment released',         desc: 'A/P payment recorded + bank/cash account updated',   color: 'emerald' },
  { id: 'kpi',         label: '11. KPI scorecard',           desc: 'completion% + SLA% feed Provider KPI letter grade',  color: 'emerald' },
];

const COLOR = {
  sky:     { dot: 'bg-sky-500',     ring: 'ring-sky-200',     pill: 'bg-sky-50 border-sky-200 text-sky-900' },
  amber:   { dot: 'bg-amber-500',   ring: 'ring-amber-200',   pill: 'bg-amber-50 border-amber-200 text-amber-900' },
  emerald: { dot: 'bg-emerald-500', ring: 'ring-emerald-200', pill: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
};

function FlowDiagram({ steps, accent = 'amber' }) {
  return (
    <div className="relative">
      {/* vertical guide line */}
      <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-sky-300 via-amber-300 to-emerald-300" />
      <ol className="space-y-3 relative">
        {steps.map((s, idx) => {
          const c = COLOR[s.color] || COLOR[accent];
          return (
            <li key={s.id} data-testid={`workflow-step-${s.id}`} className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full ${c.dot} ring-4 ${c.ring} flex items-center justify-center flex-shrink-0 z-10 text-white text-xs font-bold shadow`}>
                {idx + 1}
              </div>
              <div className={`flex-1 p-3 rounded-xl border ${c.pill} ${s.branch === 'optional' ? 'border-dashed' : ''}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{s.label}</span>
                  {s.branch === 'optional' && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-slate-200 text-slate-600">Optional</span>
                  )}
                </div>
                <p className="text-xs text-slate-600 mt-1">{s.desc}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function Workflows() {
  const [tab, setTab] = useState('complaint');
  const t = useT();
  return (
    <Layout>
      <div data-testid="workflows-page" className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-amber-600" />{t('nav.workflows')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('workflows.subtitle') === 'workflows.subtitle' ? 'Visual maps of how Complaints and Service Provider PPM contracts flow end-to-end.' : t('workflows.subtitle')}</p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="tab-complaint"
            onClick={() => setTab('complaint')}
            className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition ${tab === 'complaint' ? 'bg-amber-500 text-slate-900 shadow' : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-300'}`}
          >
            <MessageSquare className="w-4 h-4" />{t('tab.complaintFlow')}
          </button>
          <button
            type="button"
            data-testid="tab-ppm"
            onClick={() => setTab('ppm')}
            className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition ${tab === 'ppm' ? 'bg-amber-500 text-slate-900 shadow' : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-300'}`}
          >
            <ClipboardList className="w-4 h-4" />{t('tab.providerFlow')}
          </button>
        </div>

        <Card className="p-6">
          {tab === 'complaint' ? (
            <>
              <h2 className="font-semibold text-foreground mb-1">Complaint → Resolution → Payment → Closure</h2>
              <p className="text-xs text-muted-foreground mb-4">Tenant raises a complaint. Triage → assignment → service report → optional materials approval → tenant sign-off → optional invoice + payment → close.</p>
              <FlowDiagram steps={COMPLAINT_FLOW} />
            </>
          ) : (
            <>
              <h2 className="font-semibold text-foreground mb-1">AMC → Inspection → Invoice → Approval → Payment → KPI</h2>
              <p className="text-xs text-muted-foreground mb-4">Service provider PPM lifecycle, from contract signing through invoice approval and KPI scoring.</p>
              <FlowDiagram steps={PPM_FLOW} accent="emerald" />
            </>
          )}
        </Card>

        <div className="text-xs text-muted-foreground">
          Tip: branched (dashed) steps are optional — they only fire when materials are needed (Complaint flow) or when an invoice exists for the visit (PPM flow).
        </div>
      </div>
    </Layout>
  );
}
